import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { account } from "../lib/appwrite.js";
import {
  resolvePostLoginPathForRole,
  resolveRoleForUser,
} from "../lib/heycoachSubscription.js";
import { Sentry } from "../lib/sentry.js";

const AuthContext = createContext(null);

function syncSentryUser(user, role) {
  if (!user?.$id) {
    Sentry.setUser(null);
    return;
  }

  Sentry.setUser({ id: user.$id });
  if (role && role !== "loading" && role !== "unauthenticated") {
    Sentry.setTag("accountRole", role);
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState("loading");
  const [status, setStatus] = useState("loading");

  const loadSession = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setStatus("loading");
      setRole("loading");
    }
    try {
      const current = await account.get();
      const resolvedRole = await resolveRoleForUser(current);
      const result = {
        user: current,
        role: resolvedRole,
        postLoginPath: resolvePostLoginPathForRole(resolvedRole),
      };
      flushSync(() => {
        setUser(current);
        setRole(resolvedRole);
        setStatus("authenticated");
      });
      syncSentryUser(current, resolvedRole);
      return result;
    } catch {
      flushSync(() => {
        setUser(null);
        setRole("unauthenticated");
        setStatus("unauthenticated");
      });
      syncSentryUser(null);
      return { user: null, role: "unauthenticated", postLoginPath: null };
    }
  }, []);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const clearSession = useCallback(() => {
    flushSync(() => {
      setUser(null);
      setRole("unauthenticated");
      setStatus("unauthenticated");
    });
    syncSentryUser(null);
  }, []);

  const value = useMemo(() => {
    const contextRole =
      status === "loading"
        ? "loading"
        : status === "authenticated"
          ? role
          : "unauthenticated";

    return {
      user,
      status,
      role: contextRole,
      postLoginPath:
        contextRole === "client" || contextRole === "coach"
          ? resolvePostLoginPathForRole(contextRole)
          : null,
      refetch: loadSession,
      clearSession,
    };
  }, [user, status, role, loadSession, clearSession]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
